import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaPlay, FaCopy, FaEdit, FaTrash, FaSpinner } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import { getLoggedInUserId, pickRecordId } from '../../utils/dashboardRunUserLabel';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import CloneNameModal from '../common/CloneNameModal';
import EntityAssignUsersModal from '../common/EntityAssignUsersModal';
import {
  formatDashboardOwner,
  resolveDashboardOwnerId,
} from '../dashboard/dashboardFormUtils';
import { getEntitlementsSection, getLicenseTypeCodeFromSession } from '../../utils/entitlements';
import { canCreateReportByRole } from '../../utils/permissionCache';
import { formatDateTime } from '../../utils/formatUtils';
import './css/ReportsListPage.css';
import '../dashboard/css/DashboardManager.css';

const ReportsListPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneError, setCloneError] = useState(null);
  const [cloningReportId, setCloningReportId] = useState(null);
  const [assignReport, setAssignReport] = useState(null);
  const [roleAllowCreateReport, setRoleAllowCreateReport] = useState(true);
  const navigate = useNavigate();
  const canChangeReportOwner = getLicenseTypeCodeFromSession() === 'ADMIN';

  const labelToName = (label) => {
    if (!label || typeof label !== 'string') return '';
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || '';
  };

  const normalizeReportName = (name) => labelToName(name);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const allowed = await canCreateReportByRole();
      if (!cancelled) setRoleAllowCreateReport(allowed);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const reportApi = new DataApi('reports');
      const response = await reportApi.get('/');

      if (response.data.success) {
        setReports(response.data.data);
      } else {
        setError('Failed to fetch reports');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Error loading reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = () => {
    navigate('/report-builder');
  };

  const canModifyReport = (report) => {
    if (canChangeReportOwner) return true;
    const me = getLoggedInUserId();
    const ownerId = resolveDashboardOwnerId(report);
    return !!me && !!ownerId && String(me) === String(ownerId);
  };

  const openReportEditor = (report) => {
    const reportId = pickRecordId(report) || report?.id;
    if (!reportId) return;

    if (!canModifyReport(report)) {
      window.alert('You can edit only those reports where you are the owner.');
      return;
    }

    navigate(`/reportbuilderpage/${reportId}`);
  };

  const handleViewReport = (reportId) => {
    navigate(`/viewreport/${reportId}?source=reports`);
  };

  const openCloneModal = (report) => {
    const sourceLabel = report.report_label || report.name || 'Report';
    setCloneError(null);
    setCloneSource({
      report,
      defaultLabel: `${sourceLabel} (Copy)`,
      defaultName: labelToName(`${sourceLabel} (Copy)`),
    });
  };

  const closeCloneModal = () => {
    if (cloningReportId) return;
    setCloneSource(null);
    setCloneError(null);
  };

  const handleConfirmCloneReport = async ({ label, name }) => {
    const report = cloneSource?.report;
    const reportId = report ? pickRecordId(report) || report.id : '';
    if (!reportId) return;

    const normalized = normalizeReportName(name || label);
    if (
      reports.some(
        (r) =>
          normalizeReportName(r.name) === normalized &&
          String(pickRecordId(r) || r.id) !== String(reportId)
      )
    ) {
      setCloneError(`A report named "${normalized}" already exists.`);
      return;
    }

    try {
      setCloningReportId(reportId);
      setCloneError(null);
      setError(null);
      const reportApi = new DataApi('reports');
      const res = await reportApi.post(`/${reportId}/clone`, { label, name });
      const cloned = res?.data?.data;
      setCloneSource(null);
      await fetchReports();
      if (
        cloned?.id &&
        window.confirm(
          `Created "${cloned.report_label || cloned.name}".\n\nOpen the report builder to edit it now?`
        )
      ) {
        openReportEditor(cloned);
      }
    } catch (err) {
      console.error('Error cloning report:', err);
      setCloneError(
        err?.response?.data?.message || 'Failed to clone report. Please try again.'
      );
    } finally {
      setCloningReportId(null);
    }
  };

  const handleDeleteReport = (report) => {
    if (!canModifyReport(report)) {
      window.alert('You can delete only those reports where you are the owner.');
      return;
    }
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const confirmDeleteReport = async () => {
    if (!reportToDelete || isDeletingReport) return;

    const reportId = pickRecordId(reportToDelete) || reportToDelete.id;
    if (!reportId) return;

    try {
      setIsDeletingReport(true);
      const reportApi = new DataApi('reports');
      await reportApi.delete(reportId);
      await fetchReports();
      setShowDeleteModal(false);
      setReportToDelete(null);
    } catch (err) {
      console.error('Error deleting report:', err);
      window.alert(
        err?.response?.data?.message || 'Error deleting report. Please try again.'
      );
    } finally {
      setIsDeletingReport(false);
    }
  };

  const reportEntitlements = getEntitlementsSection('report', {});
  const allowCreateReport = reportEntitlements.allowCreateReport !== false;
  const maxReports = Number(reportEntitlements.maxReports);
  // Only enforce a cap when maxReports is a positive number. maxReports === 0 used to mean "default from merge" and wrongly blocked all creation (0 >= 0).
  const isReportLimitReached =
    Number.isFinite(maxReports) && maxReports > 0 && reports.length >= maxReports;
  const canCreateReport = allowCreateReport && roleAllowCreateReport && !isReportLimitReached;

  const filteredReports = reports.filter(report => {
    const owner = formatDashboardOwner(report).toLowerCase();
    const q = searchTerm.toLowerCase();
    const matchesSearch = report.name.toLowerCase().includes(q) ||
      (report.report_label && report.report_label.toLowerCase().includes(q)) ||
      report.description?.toLowerCase().includes(q) ||
      report.object_name.toLowerCase().includes(q) ||
      (report.object_label && report.object_label.toLowerCase().includes(q)) ||
      owner.includes(q);

    const matchesFilter = filterType === 'all' ||
      (filterType === 'public' && report.is_public) ||
      (filterType === 'private' && !report.is_public) ||
      (filterType === 'tabular' && report.report_type === 'tabular') ||
      (filterType === 'chart' && report.report_type === 'chart');

    return matchesSearch && matchesFilter;
  });

  const formatReportDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString);
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'tabular':
        return '📊';
      case 'chart':
        return '📈';
      case 'summary':
        return '📋';
      case 'matrix':
        return '🔢';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <div className="reports-list-page">
        <div className="reports-list-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-list-page">
        <div className="reports-list-container">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error Loading Reports</h3>
            <p>{error}</p>
            <button onClick={fetchReports} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-list-page">
      <div className="reports-list-container">
        <div className="reports-header">
          <div className="header-content">
            <h1>Reports</h1>
            <p>Manage and view all your reports</p>
          </div>
          {canCreateReport && (
            <button
              className="create-report-btn"
              onClick={handleCreateReport}
            >
              <span className="btn-icon">+</span>
              Create New Report
            </button>
          )}
        </div>

        <div className="reports-filters-row">
          <div className="reports-filters">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search reports by name, owner, description, or object…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filter-container">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Reports</option>
                <option value="public">Public Reports</option>
                <option value="private">Private Reports</option>
                <option value="tabular">Tabular Reports</option>
                <option value="chart">Chart Reports</option>
              </select>
            </div>
          </div>

          <div className="reports-stats">
            <div className="stat-item">
              <span className="stat-number">{reports.length}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{reports.filter(r => r.is_public).length}</span>
              <span className="stat-label">Public</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{filteredReports.length}</span>
              <span className="stat-label">Filtered</span>
            </div>
          </div>
        </div>

        {filteredReports.length === 0 ? (
          <div className="no-reports-container">
            <div className="no-reports-icon">📊</div>
            <h3>No Reports Found</h3>
            <p>
              {searchTerm || filterType !== 'all'
                ? 'No reports match your current filters. Try adjusting your search criteria.'
                : 'You haven\'t created any reports yet. Create your first report to get started.'
              }
            </p>
            {(!searchTerm && filterType === 'all') && canCreateReport && (
              <button
                className="create-first-report-btn"
                onClick={handleCreateReport}
              >
                Create Your First Report
              </button>
            )}
          </div>
        ) : (
          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Object</th>
                  <th>Type</th>
                  <th>Owner</th>
                  <th>Visibility</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} className="report-row">
                    <td className="report-name-cell">
                      <div className="report-name-content">
                        <span className="report-type-icon">
                          {getReportTypeIcon(report.report_type)}
                        </span>
                        <div>
                          <div className="report-name">{report.report_label || report.name}</div>
                          {report.description && (
                            <div className="report-description">
                              {report.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="object-cell">
                      {report.report_objects && report.report_objects.length > 0 ? (
                        // Show all objects from report_objects array (including primary)
                        report.report_objects.map((obj, index) => (
                          <span key={obj.id || index}>
                            {obj.object_label || obj.object_name}
                            {index < report.report_objects.length - 1 && ' & '}
                          </span>
                        ))
                      ) : (
                        // Fallback to primary object only
                        report.object_label || report.object_name
                      )}
                    </td>
                    <td className="type-cell">
                      <span className="type-badge">{report.report_type}</span>
                    </td>
                    <td className="dashboard-table-owner">
                      {formatDashboardOwner(report)}
                    </td>
                    <td className="visibility-cell">
                      {report.is_public ? (
                        <span className="visibility-badge public">Public</span>
                      ) : (
                        <span className="visibility-badge private">Private</span>
                      )}
                    </td>
                    <td className="date-cell">{report.assigned_user_count ?? 0}</td>
                    <td className="date-cell">
                      {formatReportDate(report.createddate)}
                    </td>
                    <td className="dashboard-table-actions">
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-users-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssignReport({
                            ...report,
                            id: pickRecordId(report) || report.id,
                          });
                        }}
                        title="Assign users"
                      >
                        <FaUsers />
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-run-btn"
                        onClick={() => handleViewReport(report.id)}
                        title="View Report"
                      >
                        <FaPlay />
                      </button>
                      {canCreateReport && (
                        <button
                          type="button"
                          className="dashboard-action-btn dashboard-clone-btn"
                          onClick={() => openCloneModal(report)}
                          title="Clone report"
                          disabled={cloningReportId === (pickRecordId(report) || report.id)}
                        >
                          {cloningReportId === (pickRecordId(report) || report.id) ? (
                            <FaSpinner className="fa-spin" />
                          ) : (
                            <FaCopy />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-edit-btn"
                        onClick={() => openReportEditor(report)}
                        title={
                          canModifyReport(report)
                            ? 'Edit Report'
                            : 'Only the report owner or an admin can edit'
                        }
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-delete-btn"
                        onClick={() => handleDeleteReport(report)}
                        title={
                          canModifyReport(report)
                            ? 'Delete Report'
                            : 'Only the report owner or an admin can delete'
                        }
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EntityAssignUsersModal
        show={!!assignReport}
        entity={assignReport}
        onClose={() => setAssignReport(null)}
        onSaved={fetchReports}
        apiResource="reports"
        entityTypeLabel="report"
        assignmentIdField="entity_id"
        exclusiveAssignment={false}
        introText="Admins see all reports. Owners see only their own. Other users can open this report only if listed here (assigned) or they own it."
      />

      {/* Delete Confirmation Modal */}
      <CloneNameModal
        show={!!cloneSource}
        entityType="report"
        labelCaption="Report label"
        nameCaption="Report name"
        hint="Columns, filters, chart definitions, and report settings will be copied."
        sourceLabel={
          cloneSource?.report?.report_label || cloneSource?.report?.name || ''
        }
        defaultLabel={cloneSource?.defaultLabel}
        defaultName={cloneSource?.defaultName}
        deriveNameFromLabel={labelToName}
        saving={!!cloningReportId}
        error={cloneError}
        onClose={closeCloneModal}
        onConfirm={handleConfirmCloneReport}
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          if (isDeletingReport) return;
          setShowDeleteModal(false);
          setReportToDelete(null);
        }}
        onConfirm={confirmDeleteReport}
        isDeleting={isDeletingReport}
        title="Confirm Delete"
        message="Are you sure you want to delete this report?"
        itemName={
          reportToDelete?.report_label || reportToDelete?.name || ''
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ReportsListPage;
